// Pricing tiers configuration
export type PricingTier = 'small' | 'medium' | 'large';
export type BillingInterval = 'monthly' | 'yearly';

export interface PricingTierConfig {
  id: PricingTier;
  name: string;
  memberRange: string;
  minMembers: number;
  maxMembers: number | null;
  // Yearly billing (per month) - the discounted rate
  priceExclBTW: number;
  priceInclBTW: number;
  priceInCents: number;
  priceInCentsInclBTW: number;
  // Monthly flex billing (per month) - higher rate
  monthlyPriceExclBTW: number;
  monthlyPriceInclBTW: number;
  monthlyPriceInCents: number;
  monthlyPriceInCentsInclBTW: number;
  // Yearly totals
  yearlyTotalExclBTW: number;
  yearlyTotalInclBTW: number;
  // Savings
  savingsTotal: number; // total saved per year vs monthly
  savingsPercent: number;
  isPopular: boolean;
}

export const PRICING_TIERS: PricingTierConfig[] = [
  {
    id: 'small',
    name: 'Kleine club',
    memberRange: '1 – 150 actieve clubleden',
    minMembers: 1,
    maxMembers: 150,
    // Yearly per month
    priceExclBTW: 19.95,
    priceInclBTW: 24.14,
    priceInCents: 1995,
    priceInCentsInclBTW: 2414,
    // Monthly flex
    monthlyPriceExclBTW: 34.95,
    monthlyPriceInclBTW: 42.29,
    monthlyPriceInCents: 3495,
    monthlyPriceInCentsInclBTW: 4229,
    // Yearly totals
    yearlyTotalExclBTW: 239.40,
    yearlyTotalInclBTW: 289.67,
    // Savings
    savingsTotal: 180.00,
    savingsPercent: 43,
    isPopular: false,
  },
  {
    id: 'medium',
    name: 'Gemiddelde club',
    memberRange: '151 – 450 actieve clubleden',
    minMembers: 151,
    maxMembers: 450,
    priceExclBTW: 24.95,
    priceInclBTW: 30.19,
    priceInCents: 2495,
    priceInCentsInclBTW: 3019,
    monthlyPriceExclBTW: 44.95,
    monthlyPriceInclBTW: 54.39,
    monthlyPriceInCents: 4495,
    monthlyPriceInCentsInclBTW: 5439,
    yearlyTotalExclBTW: 299.40,
    yearlyTotalInclBTW: 362.27,
    savingsTotal: 240.00,
    savingsPercent: 44,
    isPopular: true,
  },
  {
    id: 'large',
    name: 'Grote club',
    memberRange: '450+ actieve clubleden',
    minMembers: 451,
    maxMembers: null,
    priceExclBTW: 34.95,
    priceInclBTW: 42.29,
    priceInCents: 3495,
    priceInCentsInclBTW: 4229,
    monthlyPriceExclBTW: 59.95,
    monthlyPriceInclBTW: 72.54,
    monthlyPriceInCents: 5995,
    monthlyPriceInCentsInclBTW: 7254,
    yearlyTotalExclBTW: 419.40,
    yearlyTotalInclBTW: 507.47,
    savingsTotal: 300.00,
    savingsPercent: 42,
    isPopular: false,
  },
];

export const getPricingTier = (tier: PricingTier): PricingTierConfig => {
  return PRICING_TIERS.find(t => t.id === tier) || PRICING_TIERS[1]; // default to medium
};

export const formatPrice = (price: number): string => {
  return price.toFixed(2).replace('.', ',');
};

export const getTierDisplayName = (tier: PricingTier): string => {
  const config = getPricingTier(tier);
  return config.name;
};

export const getTierBadgeColor = (tier: PricingTier): string => {
  switch (tier) {
    case 'small':
      return 'bg-blue-100 text-blue-800';
    case 'medium':
      return 'bg-green-100 text-green-800';
    case 'large':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Get price details for a specific tier and billing interval
 */
export const getPriceForInterval = (tier: PricingTier, interval: BillingInterval) => {
  const config = getPricingTier(tier);
  if (interval === 'yearly') {
    return {
      perMonth: config.priceExclBTW,
      perMonthInclBTW: config.priceInclBTW,
      totalExclBTW: config.yearlyTotalExclBTW,
      totalInclBTW: config.yearlyTotalInclBTW,
      priceInCents: config.priceInCents, // per month excl BTW
      priceInCentsInclBTW: config.priceInCentsInclBTW, // per month incl BTW
      // For Mollie: yearly is charged as one payment
      mollieAmountCents: Math.round(config.yearlyTotalExclBTW * 100), // excl BTW
      mollieAmountCentsInclBTW: Math.round(config.yearlyTotalInclBTW * 100), // incl BTW
      mollieAmountFormatted: config.yearlyTotalInclBTW.toFixed(2), // incl BTW for Mollie
      interval: '12 months' as const,
      label: 'Jaarabonnement',
    };
  }
  return {
    perMonth: config.monthlyPriceExclBTW,
    perMonthInclBTW: config.monthlyPriceInclBTW,
    totalExclBTW: config.monthlyPriceExclBTW,
    totalInclBTW: config.monthlyPriceInclBTW,
    priceInCents: config.monthlyPriceInCents,
    priceInCentsInclBTW: config.monthlyPriceInCentsInclBTW,
    mollieAmountCents: config.monthlyPriceInCents,
    mollieAmountCentsInclBTW: config.monthlyPriceInCentsInclBTW,
    mollieAmountFormatted: config.monthlyPriceInclBTW.toFixed(2),
    interval: '1 month' as const,
    label: 'Maand-Flex',
  };
};

// Example cost per member calculations
export const getExampleCostsPerMember = (tier: PricingTier, interval: BillingInterval = 'yearly') => {
  const config = getPricingTier(tier);
  const price = interval === 'yearly' ? config.priceExclBTW : config.monthlyPriceExclBTW;
  const examples: { members: number; costPerMember: string }[] = [];
  
  if (tier === 'small') {
    examples.push(
      { members: 25, costPerMember: formatPrice(price / 25) },
      { members: 75, costPerMember: formatPrice(price / 75) },
      { members: 150, costPerMember: formatPrice(price / 150) }
    );
  } else if (tier === 'medium') {
    examples.push(
      { members: 200, costPerMember: formatPrice(price / 200) },
      { members: 300, costPerMember: formatPrice(price / 300) },
      { members: 450, costPerMember: formatPrice(price / 450) }
    );
  } else {
    examples.push(
      { members: 500, costPerMember: formatPrice(price / 500) },
      { members: 750, costPerMember: formatPrice(price / 750) },
      { members: 1000, costPerMember: formatPrice(price / 1000) }
    );
  }
  
  return examples;
};

export const getBillingIntervalLabel = (interval: BillingInterval): string => {
  return interval === 'yearly' ? 'Jaarabonnement' : 'Maand-Flex';
};
